import { useQueries, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import MainCard from "components/layout/MainCard/MainCard";
import Breadcrumbs from "../../components/layout/BreadCrumbs/Breadcrumbs";
import LoadingWrapper from "../../components/Loading/LoadingWrapper";
import ProductDetailsSlider from "../../components/products/ProductDetailsSlider";
import useProductFlags from "../../hooks/useProductFlags";
import BasicTable from "./BasicTable/BasicTable";
import Footer from "./Footer/Footer";
import classes from "./ProductPage.module.scss";
import useAPI from "../../hooks/useAPI";
import CatalogueAside from "../Catalogue/Aside/CatalogueAside";
import Services from "./Services/Services";
import DescriptionTabs from "./DescriptionTabs/DescriptionTabs";
import { useEffect, useState } from "react";
import ProductItems from "../Catalogue/Products/ProductItems/ProductItems";
import scss from "./BasicTable/BasicTable.module.scss";
import Variants from "../../components/Variants/Variants";
import { useTranslation } from "react-i18next";
import { isNewSticker, isFeaturedSticker } from "helpers/stickers";

/**
 * Single product page.
 */
const ProductPage = () => {
    const { t } = useTranslation();
    const api = useAPI();
    const productFlags = useProductFlags();
    const { productId } = useParams();

    // Easy access to API
    const execute = (part: string) => async () => {
        return await api.get(`/product-details/${part}/${productId}`);
    };
    const executeFetch = (part: string) => async () => {
        return await api.fetch(`/product-details/${part}/${productId}`);
    };
    const executeList = (part: string, payload: {}) => () =>
        api.list(`/product-details/${part}/${productId}`, payload).then((response) => {
            return response?.items;
        });
    // Load the product details
    const { data: basic, ...basicQuery } = useQuery(
        [`Product:${productId}:Basic`],
        async () => {
            return await api.get(`/product-details/basic-data/${productId}`).then((res) => res);
        },
        {
            cacheTime: 0,
            staleTime: 0,
        }
    );
    const { data: seo, ...seoQuery } = useQuery([`Product:${productId}:Seo`, productId], execute("seo"));
    const { data: gallery, ...galleryQuery } = useQuery([`Product:${productId}:Gallery`, productId], executeFetch("gallery"));
    const { data: sticker } = useQuery([`Product:${productId}:Sticker`, productId], executeFetch("stickers"));

    const { data: specification, ...specificationQuery } = useQuery(
        [`Product:${productId}:Specification`, productId, "product_details_specifications"],
        async () => await api.get(`/product-details/specification/${productId}?position=product_details_specifications`)
    );
    const { data: headerSpecification } = useQuery(
        [`Product:${productId}:Specification`, productId, "product_details_header_info"],
        async () => await api.get(`/product-details/specification/${productId}?position=product_details_header_info`)
    );
    const { data: description, ...descriptionQuery } = useQuery([`Product:${productId}:Description`, productId], execute("description"));
    const { data: declaration, ...declarationQuery } = useQuery([`Product:${productId}:Declaration`, productId], execute("declaration"));
    const { data: breadcrumbs, ...breadcrumbsQuery } = useQuery([`Product:${productId}:Breadcrumbs`, productId], execute("breadcrumbs"));
    const { data: technicalDoc, ...technicalDocQuery } = useQuery([`Product:${productId}:TechnicalDoc`, productId], executeList("technical-doc"));
    const { data: instructionDoc, ...instructionDocQuery } = useQuery([`Product:${productId}:InstructionDoc`, productId], executeList("instruction-doc"));
    const { data: certificateDoc, ...certificateDocQuery } = useQuery([`Product:${productId}:CertificateDoc`, productId], executeList("certificate-doc"));

    const { data: recommended, ...recommendedQuery } = useQuery([`Product:${productId}:Recommended`, productId], executeList("recommended", { limit: 4 }));
    const { data: crosssell, ...crossSellQuery } = useQuery([`Product:${productId}:CrossSell`, productId], executeList("cross-sell", { limit: 4 }));
    const { data: upsell, ...upSellQuery } = useQuery([`Product:${productId}:UpSell`, productId], executeList("up-sell", { limit: 4 }));

    // Product Details tabs configuration (order/visibility of built-in + custom tabs),
    // managed in CMS under B2B Settings -> b2b_basic_configuration -> product_details_tabs.
    // Response is an array of { slug, value }, one entry per position_N field.
    const { data: tabsConfigRaw, ...tabsConfigQuery } = useQuery([`Product:${productId}:TabsConfig`], () =>
        api.get("configuration/protected/data/b2b_basic_configuration/product_details_tabs")
    );

    // Read however many "position_N" fields the backend actually returns (however many
    // built-in + custom tab slots are configured there - not hardcoded here, so adding
    // more slots later needs no frontend change) into an ordered, compacted list of tab
    // identifiers, dropping empties.
    const tabsConfigBySlug = Object.fromEntries((tabsConfigRaw ?? []).map((field) => [field.slug, field.value]));
    const orderedTabKeys = Object.keys(tabsConfigBySlug)
        .filter((slug) => /^position_\d+$/.test(slug))
        .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]))
        .map((slug) => tabsConfigBySlug[slug])
        .filter(Boolean);

    // For every custom slot in that list, fetch its paired title/content specification positions.
    const customSlots = orderedTabKeys.filter((key) => key.startsWith("product_details_tab_"));
    const slotQueries = useQueries(
        customSlots.flatMap((slot) => [
            {
                queryKey: [`Product:${productId}:${slot}:name`],
                queryFn: () => api.get(`/product-details/specification/${productId}?position=${slot}_name`),
            },
            {
                queryKey: [`Product:${productId}:${slot}:content`],
                queryFn: () => api.get(`/product-details/specification/${productId}?position=${slot}_content`),
            },
        ])
    );

    // Pair each custom slot's fetched name/content back together, keyed by slot,
    // along with its display mode ("list" or "html") from the same config response.
    const customTabsData = {};
    customSlots.forEach((slot, index) => {
        const slotNumber = slot.replace("product_details_tab_", "");
        customTabsData[slot] = {
            name: slotQueries[index * 2]?.data,
            content: slotQueries[index * 2 + 1]?.data,
            displayMode: tabsConfigBySlug[`tab_${slotNumber}_display_mode`] || "list",
        };
    });
    const isSlotQueriesLoading = slotQueries.some((query) => query.isLoading);

    // All must be loaded
    const isLoading = basicQuery.isLoading || seoQuery.isLoading || galleryQuery.isLoading;
    const isDescriptionLodaing =
        specificationQuery.isLoading ||
        descriptionQuery.isLoading ||
        declarationQuery.isLoading ||
        technicalDocQuery.isLoading ||
        instructionDocQuery.isLoading ||
        certificateDocQuery.isLoading ||
        tabsConfigQuery.isLoading ||
        isSlotQueriesLoading;

    const productType = basic?.type ?? "single";
    // Build the product
    const product = !isLoading ? { ...basic?.data, seo, gallery: gallery?.gallery } : null;

    const productDescription = !isDescriptionLodaing
        ? { specification, description, declaration, technicalDoc, instructionDoc, certificateDoc, orderedTabKeys, customTabsData }
        : null;
    // Append the main image to gallery
    if (product && product.image) {
        product.gallery = product.gallery ?? [];
    }

    // Handle wishlist
    const inWishlist = productFlags.wishlist.includes(product?.id);

    let crumbs = [];
    let path = breadcrumbs?.start?.slug;
    crumbs[0] = { name: `${t("global-default-text-home")}`, url: "/pocetna" };
    path += "/katalog/kategorija";
    for (const item of breadcrumbs?.steps ?? []) {
        path += "/" + item.slug;
        crumbs.push({ name: item.name, url: path });
    }

    crumbs.push({ name: breadcrumbs?.end?.name, url: "#" });

    // const selected_item = product?.variant_items.find((item) => item.slug_path.includes(productId));
    const selected_item = product?.variant_items.find((item) => item.slug_path === productId);

    const [productVariant, setProductVariant] = useState(null);
    useEffect(() => {
        if (selected_item) setProductVariant(selected_item);
    }, [selected_item]);

    const [url, setURL] = useState(null);

    const handleURLChange = (url) => {
        setURL(url);
    };
    useEffect(() => {
        if (url) {
            window.history.pushState(null, null, url);
        }
    }, [url]);

    useEffect(() => {
        if (productVariant?.basic_data?.id_product_parent !== basic?.data?.item?.basic_data?.id_product) {
            setProductVariant(null);
            const timeout = setTimeout(() => {
                basicQuery.refetch();
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [productId]);
    return (
        <MainCard>
            {/* The list of available categories */}
            <CatalogueAside translateKey={"catalogue_product_details-catalogue_aside"} />

            {breadcrumbs && (
                <div className={`${classes.breadcrumbs} mobile-hidden`}>
                    <Breadcrumbs crumbs={crumbs} />
                </div>
            )}

            {/* Show the product */}
            <div className={classes.wrapper}>
                <LoadingWrapper isLoading={!product}>
                    {/* Product details */}
                    {basic?.data?.item?.slug !== undefined && !isLoading ? (
                        <section className={classes.productWrapper}>
                            {/* Main image and the gallery */}
                            <div className={classes.gallery}>
                                {sticker?.stickers?.length > 0 && (
                                    <div className={classes.stickers}>
                                        {sticker.stickers.map((st, index) =>
                                            st?.image ? (
                                                <img className={classes.stickerImage} src={st.image} alt={st.title || st.name || ""} key={st.slug || index} />
                                            ) : (
                                                <span className={`${classes.stickerText} ${isNewSticker(st) ? classes.newSticker : ""} ${isFeaturedSticker(st) ? classes.featuredSticker : ""}`} key={st.slug || index}>{st.name}</span>
                                            )
                                        )}
                                    </div>
                                )}
                                <ProductDetailsSlider product={product} />
                            </div>
                            <div className={classes.hero}>
                                {!basicQuery?.isLoading && (
                                    <>
                                        <div>
                                            <h5 key={basic?.data?.item?.basic_data?.id_product} className={classes.title}>
                                                {!productVariant?.id ? basic?.data?.item?.basic_data?.name : productVariant?.basic_data?.name}
                                            </h5>

                                            <BasicTable
                                                productVariant={productVariant}
                                                basic={basic}
                                                headerSpecification={headerSpecification}
                                                translateKey={"catalogue_product_details-default-text"}
                                            />
                                            {basic?.product_type === "variant" && (
                                                <div className={scss.wrapper}>
                                                    <Variants
                                                        product={basic?.data}
                                                        productVariant={productVariant}
                                                        productSlug={productId}
                                                        handleURLChange={handleURLChange}
                                                        setProductVariant={setProductVariant}
                                                        translateKey={"catalogue_product_details-default-text"}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <Footer translateKey={"catalogue_product_details-default"} product={product} productVariant={productVariant} inWishlist={inWishlist} />

                                        <Services />
                                    </>
                                )}
                            </div>
                        </section>
                    ) : (
                        <div className={classes.noitem}>{t("catalogue_product_details-default-text-requested_product_not_available")}</div>
                    )}
                </LoadingWrapper>
                {productDescription && <DescriptionTabs product={productDescription} translateKey={"catalogue_product_details-description"} />}
                <div className={`d-flex flex-column gap-5 mt-5`}>
                    {recommended && recommended?.length ? (
                        <div className={`w-100`}>
                            <p className={classes.title}>{t("catalogue_product_details-default-text-recommended_products_title")}</p>
                            <ProductItems products={recommended} view={"grid"} translateKey={"catalogue_product_details-default"} />
                        </div>
                    ) : (
                        ""
                    )}
                    {crosssell && crosssell?.length ? (
                        <div className={`w-100`}>
                            <p className={classes.title}>{t("catalogue_product_details-default-text-crosssell_products_title")}</p>
                            <ProductItems products={crosssell} view={"grid"} translateKey={"catalogue_product_details-default"} />
                        </div>
                    ) : (
                        ""
                    )}
                    {upsell && upsell?.length ? (
                        <div className={`w-100`}>
                            <p className={classes.title}>{t("catalogue_product_details-default-text-upsell_products_title")}</p>
                            <ProductItems products={upsell} view={"grid"} translateKey={"catalogue_product_details-default"} />
                        </div>
                    ) : (
                        ""
                    )}
                </div>
            </div>
        </MainCard>
    );
};

export default ProductPage;
